<template lang="pug">
main.modal-wrapper
    svg-icon.close-modal.cursor(name='close' size="small" @click.native="closeModal")

    main.modal-main
        header.modal-header
            sectionHeader(title="Create Transfers")

        main.transfer-form-wrapper(v-if="!displayFormCreateTransfer")
            div.modal-body.modal-content-transfer
                div.modal-info-section
                    div.form-subsection
                        sectionHeader.transfer-list-header(title="Transfers" :counter="totalTransfers")
                            template(slot="action")
                                div.btn-pale.btn--small.btn--round(
                                    :class="{'ui-disabled': disableNewTransfer }"
                                    @click.prevent="newTransfer()") New transfer

                        div.transfer-wrapper(v-if="totalMediaTransfers > 0")
                            div.transfer-type Media Platform
                            ul.transfer-list
                                li.transfer-item.l-flex-row--between(:class="{'has-errors' : mediaTransfer.errors}" v-for="(mediaTransfer, idx) of transferForms['technology-media']" :key="idx")
                                    span.transfer-label(@click="editTransfer(mediaTransfer)") {{ mediaTransfer.label }}
                                    div.l-flex-row.transfer-item-action
                                        svg-icon(name='edit' size="small" @click.native="editTransfer(mediaTransfer)" title="edit transfer")
                                        svg-icon(name='close' size="small" @click.native="removeTransfer(mediaTransfer,'technology-media')" title="remove transfer")
                                        svg-icon(v-if="mediaTransfer.errors" class="icon-errors" name='triangleExclamation' size="medium" :title="transferErrors(mediaTransfer.errors)")

                        div.transfer-wrapper(v-if="totalPixelTransfers > 0")
                            div.transfer-type Pixel URL
                            ul.transfer-list
                                li.transfer-item.l-flex-row--between(:class="{'has-errors' : pixelTransfer.errors}" v-for="(pixelTransfer, idx) of transferForms['technology-pixel']" :key="idx")
                                    span.transfer-label(@click="editTransfer(pixelTransfer)") {{ pixelTransfer.label }}
                                    div.l-flex-row.transfer-item-action
                                        svg-icon(name='edit' size="small" @click.native="editTransfer(pixelTransfer)" title="edit transfer")
                                        svg-icon(name='close' size="small" @click.native="removeTransfer(pixelTransfer,'technology-pixel')" title="remove transfer")
                                        svg-icon(v-if="pixelTransfer.errors" class="icon-errors" name='triangleExclamation' size="medium" :title="transferErrors(pixelTransfer.errors)")


            footer.modal-footer
                div.footer-content.l-flex-row--between
                    button.btn.btn--medium.btn--round(@click="closeModal()") Cancel
                    button.btn.btn--medium.btn--round.btn-rev(:class="{'ui-disabled': disableCreateButton }" @click="saveTransfers()") Create transfer(s)

        FormCreateTransfer(
            v-else
            :audienceLabel="selectedAudience.label"
            :audienceEndDate="new Date(selectedAudience.endDate)"
            :isFirstPartyInFormula="selectedAudience.has_1st_party || selectedAudience.type === 'cclone'"
            :selectedAudience="selectedAudience"
            parent="monitoring")

</template>

<script>
import { concat, size, reduce } from 'lodash'

// helpers
import { formatTransferParams } from '@/utils/helpers'

//components
import FormCreateTransfer from '@/components/common/FormCreateTransfer'
import SectionHeader from '@/components/common/SectionHeader'

//avoid repeating namespace for each store action/mutation/getter/state
import { mapMutations, mapState, mapActions } from 'vuex'

export default {
    name: 'Transfers',
    components: { FormCreateTransfer, SectionHeader },

    props: {
        selectedAudience: { type: Object, required: false, default: () => ({}) }
    },

    data() {
        return {
            processing: false
        }
    },

    watch: {
        displayFormCreateTransfer(value) {
            if (value) {
                this.processing = false
            }
        }
    },

    computed: {
        // Call to state
        ...mapState('account', {
            accountId: state => state.accountId
        }),
        ...mapState('ui/transfers', {
            transferForms: state => state.forms,
            showFormCreateTransfer: state => state.showFormCreateTransfer
        }),
        //TRANSFER
        totalPixelTransfers() {
            return size(this.transferForms['technology-pixel'])
        },
        totalMediaTransfers() {
            return size(this.transferForms['technology-media'])
        },
        totalTransfers() {
            return this.totalPixelTransfers + this.totalMediaTransfers
        },
        displayFormCreateTransfer() {
            return this.showFormCreateTransfer
        },
        disableCreateButton() {
            return this.totalTransfers === 0 || this.processing
        },
        disableNewTransfer() {
            return this.totalTransfers > 9
        }
    },

    methods: {
        ...mapMutations('ui/transfers', [
            'setTransferForm',
            'setDefaultTransferForm',
            'setTransferInEdition',
            'setIsEditTransfer',
            'setShowFormCreateTransfer'
        ]),
        ...mapActions('data/transfer', ['createTransfer']),
        ...mapActions('data/audience', ['retrieveAudience', 'editAudience']),
        ...mapActions('dashboardAccount', ['retrieveExpiringAudiences', 'retrieveExpiringTransfers']),

        closeModal() {
            this.setDefaultTransferForm()
        },

        newTransfer() {
            this.setShowFormCreateTransfer({ show: true })
        },

        editTransfer(transfer) {
            //remove transferInEdition from store to avoid duplication
            this.setTransferForm({ action: 'remove', techno: transfer.technology, transfer })

            this.setTransferInEdition({ transfer })
            this.setShowFormCreateTransfer({ show: true })
            this.setIsEditTransfer({ isEdit: true })
        },
        removeTransfer(transfer, techno) {
            this.setTransferForm({ action: 'remove', techno, transfer })
        },
        saveTransfers() {
            if (this.processing) return // Prevent multiple creation
            this.processing = true
            const mediaTransfers = reduce(
                this.transferForms['technology-media'],
                (memo, mediaTypeValue) => {
                    return [...memo, formatTransferParams(this.selectedAudience, mediaTypeValue, 'media')]
                },
                []
            )
            const pixelTransfers = reduce(
                this.transferForms['technology-pixel'],
                (memo, pixelTypeValue) => {
                    return [...memo, formatTransferParams(this.selectedAudience, pixelTypeValue, 'pixel')]
                },
                []
            )
            const transferParams = {
                data_transfers: concat([], mediaTransfers, pixelTransfers),
                audienceId: this.selectedAudience.id,
                editAudience: true
            }
            this.createTransfer(transferParams).then(transfer => {
                this.processing = false
                if (this.selectedAudience.status === 'active') {
                    this.retrieveAudience({
                        audienceId: this.selectedAudience.id,
                        fromTransfer: true,
                    }).then(() => {
                        this.retrieveExpiringAudiences()
                        this.retrieveExpiringTransfers()
                    })
                } else if (this.selectedAudience.status === 'inactive') {
                    this.editAudience({
                        id: this.selectedAudience.id,
                        notificationMessage: 'Audience status has been updated',
                        params: {
                            status: 'active'
                        }
                    }).then(() => {
                        this.retrieveExpiringAudiences()
                        this.retrieveExpiringTransfers()
                    })
                }
                if (transfer.status === 'OK') this.closeModal()

            }
            )
        },
        transferErrors(errors) {
            const errorList = []
            Object.keys(errors).forEach(key => {
                const msgKey = key === 'DataTransfer' ? 'A data transfer' : key === 'label' ? 'The ' + key  : key
                errorList.push(msgKey + ' ' + errors[key] + '. ')
            })
            return errorList.toString().split(',').join('')
        }
    }
}
</script>
